import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { auth, storage } from '../config/firebase';
import { compressImage } from './imageCompression';

/**
 * Upload a file to Firebase Storage
 * @param file - The file to upload
 * @param path - Storage path (e.g., 'academies/{academyId}/logo.png')
 * @returns Promise with the download URL
 */
export async function uploadFile(file: File, path: string): Promise<string> {
  try {
    const storageRef = ref(storage, path);
    const snapshot = await uploadBytes(storageRef, file, {
      contentType: file.type || undefined,
    });
    const downloadURL = await getDownloadURL(snapshot.ref);
    return downloadURL;
  } catch (error: any) {
    console.error('Error uploading file:', error);
    throw new Error(error.message || 'Failed to upload file');
  }
}

/**
 * Upload club logo
 */
export async function uploadClubLogo(file: File, clubId: string): Promise<string> {
  const { file: out, ext } = await compressImage(file, 'logo');
  return uploadFile(out, `clubs/${clubId}/logo.${ext}`);
}

/**
 * Upload club banner
 */
export async function uploadClubBanner(file: File, clubId: string): Promise<string> {
  const { file: out, ext } = await compressImage(file, 'banner');
  return uploadFile(out, `clubs/${clubId}/banner.${ext}`);
}

/**
 * Upload organization logo
 */
export async function uploadOrganizationLogo(file: File, organizationId: string): Promise<string> {
  const { file: out, ext } = await compressImage(file, 'logo');
  return uploadFile(out, `organizations/${organizationId}/logo.${ext}`);
}

/**
 * Upload organization banner
 */
export async function uploadOrganizationBanner(file: File, organizationId: string): Promise<string> {
  const { file: out, ext } = await compressImage(file, 'banner');
  return uploadFile(out, `organizations/${organizationId}/banner.${ext}`);
}

/**
 * Upload organization gallery image
 */
export async function uploadOrganizationGalleryImage(
  file: File,
  organizationId: string,
  index: number | string
): Promise<string> {
  const { file: out, ext } = await compressImage(file, 'gallery');
  return uploadFile(out, `organizations/${organizationId}/gallery/${index}.${ext}`);
}

/**
 * Upload academy logo
 */
export async function uploadAcademyLogo(file: File, academyId: string): Promise<string> {
  const { file: out, ext } = await compressImage(file, 'logo');
  return uploadFile(out, `academies/${academyId}/logo.${ext}`);
}

/**
 * Upload academy banner
 */
export async function uploadAcademyBanner(file: File, academyId: string): Promise<string> {
  const { file: out, ext } = await compressImage(file, 'banner');
  return uploadFile(out, `academies/${academyId}/banner.${ext}`);
}

/**
 * Upload gallery image
 */
export async function uploadGalleryImage(
  file: File,
  academyId: string,
  index: number | string
): Promise<string> {
  const { file: out, ext } = await compressImage(file, 'gallery');
  return uploadFile(out, `academies/${academyId}/gallery/${index}.${ext}`);
}

/**
 * Lesson cover image for public browse cards
 */
export async function uploadLessonImage(file: File, academyId: string, lessonId: string): Promise<string> {
  const { file: out, ext } = await compressImage(file, 'lesson');
  return uploadFile(out, `academies/${academyId}/lessons/${lessonId}.${ext}`);
}

/**
 * Program cover image (fallback for linked lessons on public browse)
 */
export async function uploadProgramImage(file: File, academyId: string, programId: string): Promise<string> {
  const { file: out, ext } = await compressImage(file, 'program');
  return uploadFile(out, `academies/${academyId}/programs/${programId}.${ext}`);
}

/**
 * Play event cover image for public browse cards and detail modal
 */
export async function uploadPlayEventImage(
  file: File,
  organizationId: string,
  eventId: string
): Promise<string> {
  const { file: out, ext } = await compressImage(file, 'play_event');
  return uploadFile(out, `organizations/${organizationId}/play-events/${eventId}.${ext}`);
}

/**
 * Upload user avatar (profiles/{firebaseUid}/avatar.{ext})
 * Storage rules require the path segment to match request.auth.uid — use Firebase Auth UID,
 * not the platform users.id (they differ after refreshUserProfile()).
 */
export async function uploadUserAvatar(file: File, uid?: string): Promise<string> {
  const firebaseUid = auth.currentUser?.uid ?? uid;
  if (!firebaseUid || typeof firebaseUid !== 'string' || firebaseUid.trim() === '') {
    throw new Error('User ID is required to upload avatar. Please sign in again.');
  }
  const fileExtension = file.name.split('.').pop() || 'png';
  const path = `profiles/${firebaseUid}/avatar.${fileExtension}`;
  try {
    return await uploadFile(file, path);
  } catch (error: unknown) {
    const message = error instanceof Error ? error.message : 'Unknown error';
    // Provide helpful hints for common failures
    if (message.includes('unavailable') || message.includes('connection') || message.includes('ECONNREFUSED') || message.includes('Failed to fetch')) {
      throw new Error(
        'Storage unavailable. In development, ensure Firebase emulator is running: npm run dev:firebase'
      );
    }
    if (message.includes('permission') || message.includes('unauthorized') || message.includes('403')) {
      throw new Error('Permission denied. Please sign in again and try uploading.');
    }
    throw new Error(message || 'Failed to upload avatar');
  }
}

export default storage;
